<template>
	<!-- eslint-disable max-lines-->
	<Modal
		max-width="1000px"
		:show-search-field="availableEndpoints.length > 3"
		:search-query.sync="searchQuery"
		search-query-label="Service Endpoints"
	>
		<template #modal-title>
			<IconButton
				class="mr-3"
				data-testid="back-button"
				@click="$emit('back')"
			>
				mdi-arrow-left
			</IconButton>
			<span class="title">
				Choose Integration Template
			</span>
			<v-spacer />
			<IconButton
				data-testid="close-button"
				@click="$emit('close')"
			>
				mdi-close
			</IconButton>
		</template>

		<template #modal-content>
			<div
				class="service-template-selection"
				:class="{ 'with-search': availableEndpoints.length > 3 }"
			>
				<div
					v-if="filteredEndpoints.length === 0 && availableEndpoints.length > 0"
					class="no-results-message"
				>
					<v-alert
						type="info"
						outlined
						class="mb-4"
					>
						<div class="d-flex align-center">
							<v-icon class="mr-3">
								mdi-magnify
							</v-icon>
							<div>
								<div class="font-weight-bold mb-1">
									No endpoints found
								</div>
								<div class="body-2">
									No endpoints match your search criteria. Try a different search term.
								</div>
							</div>
						</div>
					</v-alert>
				</div>

				<div
					v-if="availableEndpoints.length === 0"
					class="no-templates-message"
				>
					<div class="empty-state">
						<div class="empty-state-icon">
							<v-icon
								size="64"
								color="grey lighten-2"
							>
								mdi-file-search-outline
							</v-icon>
						</div>
						<div class="empty-state-content">
							<h3 class="empty-state-title">
								No templates found
							</h3>
							<p class="empty-state-description">
								We couldn't find any templates matching your search. Try different keywords or browse all templates.
							</p>
							<div class="empty-state-actions">
								<v-btn
									outlined
									color="primary"
									small
									class="mr-2"
									@click="searchQuery = ''"
								>
									Clear search
								</v-btn>
								<v-btn
									text
									color="primary"
									small
									@click="$emit('select-custom')"
								>
									Create custom
								</v-btn>
							</div>
						</div>
					</div>
				</div>

				<div
					class="endpoints-grid"
					:class="{
						'two-column': filteredEndpoints.length <= 4 && filteredEndpoints.length > 1,
						'single-item': filteredEndpoints.length === 1
					}"
				>
					<ServiceEndpointCard
						v-for="endpoint in filteredEndpoints"
						:key="`${endpoint.templateId}-${endpoint.name}`"
						:endpoint="endpoint"
						:selected="selectedEndpointId === `${endpoint.templateId}-${endpoint.name}`"
						:data-testid="`service-endpoint-${endpoint.templateId}-${endpoint.name}`"
						@select="selectEndpoint(endpoint)"
					/>
				</div>
			</div>
			<div class="footer-content mt-3">
				<!-- Pagination -->
				<div
					v-if="showPagination"
					class="pagination-section"
				>
					<div class="footer-row">
						<div class="spacer" />
						<div class="pagination-wrapper">
							<!-- Custom dot pagination -->
							<div class="dot-pagination">
								<button
									class="nav-arrow"
									:disabled="currentPage === 1"
									@click="currentPage--"
								>
									<v-icon small>
										mdi-chevron-left
									</v-icon>
								</button>

								<div class="dots-container">
									<button
										v-for="page in totalPages"
										:key="page"
										class="page-dot"
										:class="{ active: page === currentPage }"
										@click="currentPage = page"
									/>
								</div>

								<button
									class="nav-arrow"
									:disabled="currentPage === totalPages"
									@click="currentPage++"
								>
									<v-icon small>
										mdi-chevron-right
									</v-icon>
								</button>
							</div>
						</div>
						<div class="custom-setup-link">
							Can't find what you're looking for? Try <a
								class="primary--text"
								@click="$emit('select-custom')"
							>
								custom setup
							</a>
						</div>
					</div>
				</div>
				<div
					v-if="!showPagination"
					class="footer-bottom"
				>
					<div class="spacer" />
					<div class="custom-setup-link">
						Can't find what you're looking for? Try <a
							class="primary--text"
							@click="$emit('select-custom')"
						>
							custom setup
						</a>
					</div>
				</div>
			</div>
		</template>

		<template #modal-footer />
	</Modal>
</template>

<script>
/* eslint-disable max-lines */
import Modal from '../../../../../shared/components/modal.vue'
import IconButton from '../../../../../shared/components/icon-button.vue'
import ServiceEndpointCard from './service-endpoint-card.vue'
import { SYSTEM_TEMPLATES } from '../../../../../shared/lib/system-integration-templates.js'
import { TEMPLATE_CATEGORIES } from '../../../../../shared/lib/service-templates/constants.js'

export default {
	name: 'service-template-selection-modal',
	components: {
		Modal,
		IconButton,
		ServiceEndpointCard
	},
	props: {
		systemName: {
			type: String,
			required: true
		},
		systemTemplate: {
			type: Object,
			default: null
		}
	},
	data () {
		return {
			selectedEndpointId: null,
			searchQuery: '',
			currentPage: 1,
			itemsPerPage: 6 // 3 columns x 2 rows
		}
	},
	computed: {
		availableEndpoints () {
			const endpoints = []

			// Get all compatible templates
			const compatibleTemplates = this.getCompatibleTemplates()
			// Extract all endpoints from compatible templates
			compatibleTemplates.forEach(template => {
				if (template.endpoints && template.endpoints.length > 0) {
					template.endpoints.forEach(endpoint => {
						endpoints.push({
							...endpoint,
							template: template,
							templateId: template.id
						})
					})
				}
			})

			return endpoints
		},
		allFilteredEndpoints () {
			if (!this.searchQuery || this.searchQuery.trim() === '') {
				return this.availableEndpoints
			}

			const query = this.searchQuery.toLowerCase().trim()
			return this.availableEndpoints.filter(endpoint => {
				// Search in endpoint name
				if (endpoint.name && endpoint.name.toLowerCase().includes(query)) {
					return true
				}
				// Search in endpoint description
				if (endpoint.description && endpoint.description.toLowerCase().includes(query)) {
					return true
				}
				// Search in template name
				if (endpoint.template && endpoint.template.name && endpoint.template.name.toLowerCase().includes(query)) {
					return true
				}
				// Search in HTTP method
				if (endpoint.method && endpoint.method.toLowerCase().includes(query)) {
					return true
				}
				// Search in URL/path
				if (endpoint.url && endpoint.url.toLowerCase().includes(query)) {
					return true
				}
				if (endpoint.path && endpoint.path.toLowerCase().includes(query)) {
					return true
				}
				return false
			})
		},
		filteredEndpoints () {
			const start = (this.currentPage - 1) * this.itemsPerPage
			const end = start + this.itemsPerPage
			return this.allFilteredEndpoints.slice(start, end)
		},
		totalPages () {
			return Math.ceil(this.allFilteredEndpoints.length / this.itemsPerPage)
		},
		showPagination () {
			return this.allFilteredEndpoints.length > this.itemsPerPage
		}
	},
	watch: {
		searchQuery () {
			// Reset to first page when search changes
			this.currentPage = 1
		}
	},
	methods: {
		getCompatibleTemplates () {
			return SYSTEM_TEMPLATES.filter(template => {
				// Must have endpoints to be useful as a service template
				if (!template.endpoints || template.endpoints.length === 0) {
					return false
				}

				// Exact match with system template
				if (template?.id === this.systemTemplate?.id) {
					return true
				}

				// Category-based matching
				if (template.category === TEMPLATE_CATEGORIES.GENERIC) {
					return true
				}

				// Some templates are compatible across categories
				// const universalTemplates = ['slack', 'segment', 'mixpanel', 'onesignal', 'splunk', 'generic']
				// if (universalTemplates.includes(template.id)) {
				// 	return true
				// }

				return false
			})
		},
		selectEndpoint (endpoint) {
			this.selectedEndpointId = `${endpoint.templateId}-${endpoint.name}`
			// Emit selection after a brief delay to show selection state
			setTimeout(() => {
				// Create a service template object that includes both the template and specific endpoint
				const serviceTemplate = {
					...endpoint.template,
					selectedEndpoint: endpoint,
					serviceEndpoint: {
						name: endpoint.name,
						url: endpoint.url,
						method: endpoint.method,
						description: endpoint.description
					}
				}
				this.$emit('template-selected', serviceTemplate)
			}, 200)
		}
	}
}
</script>

<style scoped>
.service-template-selection {
  min-height: 400px;
}

.service-template-selection.with-search {
  padding-top: 0;
}

.search-container {
  position: sticky;
  top: -24px;
  z-index: 10;
  background-color: white;
  padding: 24px 24px 16px 24px;
  margin: -24px -24px 16px -24px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.search-field {
  max-width: 100%;
}

.endpoints-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  max-width: 1232px; /* 3 * 400px + 2 * 16px gap */
  gap: 16px;
  min-height: 450px; /* Fixed height for 2 rows to prevent modal jumping (taller for URL content) */
  align-content: start;
  margin: 0 auto;
  justify-items: stretch;
}

/* Make every grid item consume full column width and allow shrinking */
.endpoints-grid > * { width: 100% !important; min-width: 0; }
/* Ensure the actual card inside also stretches to column width (penetrate child component) */
.endpoints-grid ::v-deep .v-card { width: 100% !important; min-width: 0; }
/* Prevent long inline code/URL from forcing wider widths; ellipsize within card */
.endpoints-grid ::v-deep pre,
.endpoints-grid ::v-deep code { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.endpoints-grid.two-column {
  grid-template-columns: repeat(2, 1fr);
  max-width: 820px; /* 2 * 400px + 1 * 16px gap */
  justify-content: center;
}

.endpoints-grid.single-item {
  grid-template-columns: 1fr;
  max-width: 400px;
  justify-content: center;
  justify-items: center;
}

@media (min-width: 1200px) {
  .endpoints-grid {
    grid-template-columns: repeat(3, 1fr);
  }

  .endpoints-grid.two-column {
    grid-template-columns: repeat(2, 1fr);
    max-width: 820px;
  }

  .endpoints-grid.single-item {
    grid-template-columns: 1fr;
    max-width: 400px;
  }
}.no-templates-message {
  text-align: center;
  padding: 20px;
}

@media (max-width: 1024px) and (min-width: 769px) {
  .endpoints-grid {
    grid-template-columns: repeat(2, 1fr);
    min-height: 550px; /* Medium height for two columns with URLs */
  }
}

.no-results-message {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  padding: 40px 20px;
}

.empty-state {
  text-align: center;
  max-width: 400px;
}

.empty-state-icon {
  margin-bottom: 24px;
  opacity: 0.7;
}

.empty-state-content {
  color: rgba(0, 0, 0, 0.87);
}

.empty-state-title {
  font-size: 1.25rem;
  font-weight: 500;
  margin-bottom: 8px;
  color: rgba(0, 0, 0, 0.87);
}

.empty-state-description {
  font-size: 0.875rem;
  line-height: 1.5;
  color: rgba(0, 0, 0, 0.6);
  margin-bottom: 24px;
}

.empty-state-actions {
  display: flex;
  justify-content: center;
  gap: 8px;
  flex-wrap: wrap;
}

/* Footer styles */
.footer-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}

.pagination-section {
  width: 100%;
}

.pagination-info {
  text-align: center;
  margin-top: 12px;
}

.pagination-info-row {
  display: flex;
  align-items: center;
  margin-top: 12px;
  width: 100%;
  position: relative;
}

.pagination-info-row span {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  text-align: center;
}

.footer-bottom-row {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: 8px;
  width: 100%;
}

.footer-row {
  display: flex;
  align-items: center;
  width: 100%;
  position: relative;
  min-height: 40px;
}

.pagination-wrapper {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  justify-content: center;
  z-index: 1;
}

/* Custom dot pagination */
.dot-pagination {
  display: flex;
  align-items: center;
  gap: 12px;
}

.nav-arrow {
  background: none;
  border: none;
  padding: 4px;
  border-radius: var(--radius-full, 9999px);
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: rgba(0, 0, 0, 0.54);
  transition: all 0.2s ease;
}

.nav-arrow:hover:not(:disabled) {
  background-color: rgba(0, 0, 0, 0.04);
  color: rgba(0, 0, 0, 0.87);
}

.nav-arrow:disabled {
  color: rgba(0, 0, 0, 0.26);
  cursor: not-allowed;
}

.dots-container {
  display: flex;
  align-items: center;
  gap: 8px;
}

.page-dot {
  width: 8px;
  height: 8px;
  border-radius: var(--radius-full, 9999px);
  border: none;
  background-color: rgba(0, 0, 0, 0.26);
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0;
}

.page-dot:hover {
  background-color: rgba(0, 0, 0, 0.54);
  transform: scale(1.2);
}

.page-dot.active {
  background-color: rgba(0, 0, 0, 0.60);
  transform: scale(1.1);
}

.footer-bottom {
  display: flex;
  justify-content: flex-end;
  width: 100%;
  align-items: flex-end;
}

.spacer {
  flex: 1;
}

.custom-setup-link {
  color: rgba(0, 0, 0, 0.6);
  font-size: 0.75rem;
  white-space: nowrap;
  align-self: center;
  margin-left: auto;
}

.CodeMirror-wrap {
	height: 100%;
}
</style>
